import React, { useRef, useEffect, useState } from 'react';
import { Box, Typography, Button, Link } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import gsap from 'gsap';
import ScrollFadeWrapper, {
  ScrollFadeSection,
} from '../../ScrollFadeWrapper/ScrollFadeWrapper';
import LinkWithIconComponent from '../LinkWithIcon/LinkWithIconComponent';
import Image from '../../Image/Image';

// Cloudflare vector for the green background shape
const FOUR_COLUMN_VECTOR_URL =
  'https://assets.askhippo.co.za/Vectors/FourColumnCardsVector.svg';

// Cloudflare icon for the arrow
const ARROW_ICON_URL =
  'https://assets.askhippo.co.za/Icons/common/Tailless-Line-Arrow-Right-Circle.svg';

const CARD_WIDTH = 295;
const CARD_GAP = 24;
const MAX_CENTERED_CARD_COUNT = 4;
const SCROLLABLE_SECTION_WIDTH = 1216;

const getCenteredSectionWidth = (cardCount) =>
  CARD_WIDTH * cardCount + CARD_GAP * (cardCount - 1) + 64;

const getInitialScrollLeft = (viewportWidth, centeredSectionWidth) => {
  const currentStart = Math.max(
    32,
    (viewportWidth - SCROLLABLE_SECTION_WIDTH) / 2 + 32
  );
  const centeredStart = Math.max(
    32,
    (viewportWidth - centeredSectionWidth) / 2 + 32
  );

  return Math.max(0, currentStart - centeredStart);
};

const ScrollingCardsComponent = ({ data }) => {
  const { title, description, footer, cards, footerLink } = data;
  const showScrollbar = data?.showScrollbar ?? true;
  const cardCount = (cards || []).length;
  const shouldShowScrollbar = showScrollbar && cardCount > 3;
  const useCenteredGrid =
    !shouldShowScrollbar &&
    cardCount > 0 &&
    cardCount <= MAX_CENTERED_CARD_COUNT;
  const centeredSectionWidth = getCenteredSectionWidth(
    useCenteredGrid ? cardCount : MAX_CENTERED_CARD_COUNT
  );
  const hasFooter = Boolean(String(footer || '').trim());
  const cardBottomPadding = hasFooter
    ? '100px'
    : shouldShowScrollbar
      ? '40px'
      : '32px';
  const scrollbarBottom = hasFooter ? '56px' : '8px';
  const navigate = useNavigate();
  const scrollRef = useRef(null);
  const trackRef = useRef(null);

  // button refs per card
  const buttonRefs = useRef([]);

  const [thumbLeft, setThumbLeft] = useState(0);
  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const initialScrollLeft = useRef(0);

  const isCardDragging = useRef(false);
  const cardDragStartX = useRef(0);
  const cardInitialScrollLeft = useRef(0);
  const cardDragMoved = useRef(false);

  const TRACK_WIDTH = 596;
  const THUMB_WIDTH = 183;

  // bounce animation that resolves when done
  const gs_bounce = (target, strength = 0.85) => {
    if (!target) return Promise.resolve();
    return new Promise((resolve) => {
      const tl = gsap.timeline({
        onComplete: resolve,
        defaults: { transformOrigin: '50% 50%' },
      });
      tl.to(target, {
        scale: strength,
        duration: 0.15,
        ease: 'power3.inOut',
      }).to(target, {
        scale: 1,
        duration: 0.85,
        ease: 'elastic.out(1.75, 0.4)',
      });
    });
  };

  const handleButtonClick = async (index, link) => {
    const prefersReduced =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // ✅ scroll to top first
    if (typeof window !== 'undefined') {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: prefersReduced ? 'auto' : 'smooth',
      });
    }

    // keep your bounce
    if (!prefersReduced) {
      await gs_bounce(buttonRefs.current[index]);
    }

    navigate(link);
  };

  const updateThumb = () => {
    const el = scrollRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    if (maxScroll <= 0) {
      setThumbLeft(0);
      return;
    }
    const scrollRatio = el.scrollLeft / maxScroll;
    const maxThumbLeft = TRACK_WIDTH - THUMB_WIDTH;
    setThumbLeft(scrollRatio * maxThumbLeft);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    updateThumb();
    el.addEventListener('scroll', updateThumb);
    window.addEventListener('resize', updateThumb);

    return () => {
      el.removeEventListener('scroll', updateThumb);
      window.removeEventListener('resize', updateThumb);
    };
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !shouldShowScrollbar) return;
    const blockClickOnDrag = (e) => {
      if (cardDragMoved.current) {
        e.stopPropagation();
        e.preventDefault();
      }
    };
    el.addEventListener('click', blockClickOnDrag, true);
    return () => el.removeEventListener('click', blockClickOnDrag, true);
  }, [shouldShowScrollbar]);

  useEffect(() => {
    if (!shouldShowScrollbar || useCenteredGrid) return;

    const el = scrollRef.current;
    if (!el || typeof window === 'undefined') return;

    const frame = window.requestAnimationFrame(() => {
      const maxScroll = Math.max(0, el.scrollWidth - el.clientWidth);
      el.scrollLeft = Math.min(
        getInitialScrollLeft(window.innerWidth, centeredSectionWidth),
        maxScroll
      );
      updateThumb();
    });

    return () => window.cancelAnimationFrame(frame);
  }, [
    shouldShowScrollbar,
    useCenteredGrid,
    cards?.length,
    centeredSectionWidth,
  ]);

  const handleMouseDown = (e) => {
    isDragging.current = true;
    dragStartX.current = e.clientX;
    initialScrollLeft.current = scrollRef.current.scrollLeft;
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!isDragging.current) return;
    const deltaX = e.clientX - dragStartX.current;
    const el = scrollRef.current;
    if (!el) return;

    const maxScroll = el.scrollWidth - el.clientWidth;
    const maxThumbLeft = TRACK_WIDTH - THUMB_WIDTH;
    if (maxThumbLeft <= 0) return;

    const scrollDelta = (deltaX / maxThumbLeft) * maxScroll;
    el.scrollLeft = initialScrollLeft.current + scrollDelta;
  };

  const handleMouseUp = () => {
    isDragging.current = false;
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };

  const handleCardMouseDown = (e) => {
    if (e.button !== 0) return;
    isCardDragging.current = true;
    cardDragMoved.current = false;
    cardDragStartX.current = e.clientX;
    cardInitialScrollLeft.current = scrollRef.current?.scrollLeft ?? 0;
    document.addEventListener('mousemove', handleCardMouseMove);
    document.addEventListener('mouseup', handleCardMouseUp);
  };

  const handleCardMouseMove = (e) => {
    if (!isCardDragging.current) return;
    const delta = e.clientX - cardDragStartX.current;
    if (Math.abs(delta) > 3) cardDragMoved.current = true;
    const el = scrollRef.current;
    if (el) el.scrollLeft = cardInitialScrollLeft.current - delta;
  };

  const handleCardMouseUp = () => {
    isCardDragging.current = false;
    document.removeEventListener('mousemove', handleCardMouseMove);
    document.removeEventListener('mouseup', handleCardMouseUp);
    setTimeout(() => {
      cardDragMoved.current = false;
    }, 0);
  };

  const handleTrackClick = (e) => {
    const rect = trackRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percent = clickX / TRACK_WIDTH;
    const el = scrollRef.current;
    if (!el) return;

    const newScrollLeft = percent * (el.scrollWidth - el.clientWidth);
    el.scrollLeft = newScrollLeft;
  };

  const renderCard = (card, index) => (
    <ScrollFadeWrapper
      key={index}
      delay={0.4 + index * 0.1}
      sx={{
        height: '100%',
        display: 'flex',
        position: 'relative',
        overflow: 'visible',
        zIndex: 0,
        '&:hover': {
          zIndex: 3,
        },
      }}
    >
      <Box
        sx={{
          width: '295px',
          height: '100%',
          boxSizing: 'border-box',
          backgroundColor: '#FFFFFF',
          borderRadius: '48px',
          padding: '32px',
          boxShadow: '0px 1px 2px 0px #A2ACB133',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          flexShrink: 0,
          position: 'relative',
          transition: 'box-shadow 0.3s ease, transform 0.3s ease',
          '&:hover': {
            transform: 'scale(1.03)',
            boxShadow: '0px 6px 16px 0px #162F3B1F',
            zIndex: 1,
            '& .MuiButton-root': {
              backgroundColor: '#C7CA00',
            },
          },
        }}
      >
        {/* Icon + Title Block */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            mb: '16px',
            minHeight: '180px',
          }}
        >
          <Box
            sx={{
              position: 'relative',
              mb: '32px',
              width: '102px',
              height: '102px',
            }}
          >
            <Image
              src={FOUR_COLUMN_VECTOR_URL}
              alt="Comparison cards graphic"
              width={102}
              height={102}
              sx={{ width: '100%', height: '100%' }}
            />
            <Image
              src={card.icon}
              alt={card.imgAltTag || card.title}
              title={card.imageAltTitle || card.title}
              width={48}
              height={48}
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '48px',
                height: '48px',
              }}
            />
          </Box>

          <Typography
            component={'h3'}
            sx={{
              fontWeight: 700,
              fontSize: '1.25rem',
              color: '#162F3B',
              whiteSpace: 'pre-line',
            }}
          >
            {card.title}
          </Typography>
        </Box>

        {/* Description */}
        <Typography
          component={'p'}
          sx={{
            fontSize: '0.875rem',
            fontWeight: 400,
            color: '#162F3B',
            textAlign: 'left',
            width: '100%',
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 9,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {card.body}
        </Typography>

        {card.buttonText !== null && (
          <Box
            sx={{
              mt: 'auto',
              display: 'flex',
              justifyContent: 'center',
              width: '100%',
            }}
          >
            <Button
              ref={(el) => (buttonRefs.current[index] = el)}
              variant="contained"
              disableElevation
              onClick={() => handleButtonClick(index, card.link)}
              endIcon={
                <Image
                  src={ARROW_ICON_URL}
                  alt="Arrow icon"
                  width={24}
                  height={24}
                  sx={{ width: 24, height: 24 }}
                />
              }
              sx={{
                width: '231px',
                minHeight: '48px',
                mt: '64px',
                gap: '4px',
                borderRadius: '24px',
                backgroundColor: '#E3E6E8',
                textTransform: 'none',
                willChange: 'transform',
                '&:hover': { backgroundColor: '#b7bc00' },
              }}
            >
              <Typography
                component={'a'}
                sx={{
                  fontWeight: 700,
                  fontSize: '1.25rem',
                  color: '#162F3B',
                }}
              >
                {card.buttonText || 'Compare quotes'}
              </Typography>
            </Button>
          </Box>
        )}

        {card.buttonText === null && card.readMore && card.link && (
          <Box
            sx={{
              mt: 'auto',
              pt: '24px',
              display: 'flex',
              justifyContent: 'center',
              width: '100%',
            }}
          >
            <Link
              href={card.link}
              target="_blank"
              rel="noopener noreferrer"
              underline="none"
              sx={{
                fontWeight: 600,
                fontSize: '0.875rem',
                lineHeight: '1.25rem',
                color: '#A2ACB1',
                textAlign: 'center',
                '&:hover': {
                  color: '#9EA900',
                  textDecoration: 'underline',
                },
              }}
            >
              Read more
            </Link>
          </Box>
        )}
      </Box>
    </ScrollFadeWrapper>
  );

  return (
    <ScrollFadeSection
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <Box
        sx={{
          width: '1216px',
          maxWidth: '1216px',
          px: '32px',
          borderRadius: '48px',
          boxSizing: 'border-box',
          textAlign: 'center',
          overflow: 'visible',
        }}
      >
        {/* Title */}
        <ScrollFadeWrapper variant="text">
          <Typography
            component={'h3'}
            sx={{
              fontSize: '3rem',
              fontWeight: 700,
              color: '#162F3B',
              mb: '16px',
              whiteSpace: 'pre-line',
              lineHeight: '52px',
            }}
          >
            {title}
          </Typography>
        </ScrollFadeWrapper>

        {/* Description */}
        <ScrollFadeWrapper variant="text" delay={0.2}>
          <Typography
            component={'p'}
            sx={{
              fontSize: '1.5rem',
              letterSpacing: '-0.03em',
              lineHeight: '28px',
              fontWeight: 400,
              maxWidth: '1010px',
              width: '100%',
              color: '#162F3B',
              mb: '40px',
              mx: 'auto',
              whiteSpace: 'pre-line',
            }}
          >
            {description}
          </Typography>
        </ScrollFadeWrapper>

        {/* Scroll Area and Fixed Footer Section */}
        <Box
          sx={{
            position: 'relative',
            minHeight: hasFooter ? '500px' : 'auto',
          }}
        >
          {useCenteredGrid ? (
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                pt: '16px',
                pb: cardBottomPadding,
                boxSizing: 'border-box',
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  width: '100%',
                  gridTemplateColumns: `repeat(${cardCount}, ${CARD_WIDTH}px)`,
                  columnGap: `${CARD_GAP}px`,
                  justifyContent: 'center',
                  alignItems: 'stretch',
                }}
              >
                {cards.map(renderCard)}
              </Box>
            </Box>
          ) : (
            <Box
              ref={scrollRef}
              onMouseDown={handleCardMouseDown}
              sx={{
                width: '100vw',
                maxWidth: '100vw',
                boxSizing: 'border-box',
                mx: 'calc(50% - 50vw)',
                pl: 'max(32px, calc((100vw - 1216px) / 2 + 32px))',
                pr: '32px',
                overflowX: 'auto',
                overflowY: 'hidden',
                pt: '16px',
                pb: cardBottomPadding,
                display: 'grid',
                gridAutoFlow: 'column',
                gridAutoColumns: `${CARD_WIDTH}px`,
                gridAutoRows: '1fr',
                columnGap: `${CARD_GAP}px`,
                alignItems: 'stretch',
                '&::-webkit-scrollbar': { display: 'none' },
                scrollbarWidth: 'none',
                cursor: 'grab',
                userSelect: 'none',
              }}
            >
              {cards.map(renderCard)}
            </Box>
          )}

          {shouldShowScrollbar && (
            <ScrollFadeWrapper delay={0.1}>
              <Box
                sx={{
                  position: 'absolute',
                  bottom: scrollbarBottom,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  display: 'flex',
                  justifyContent: 'center',
                  width: '100%',
                }}
              >
                <Box
                  ref={trackRef}
                  onClick={handleTrackClick}
                  sx={{
                    width: `${TRACK_WIDTH}px`,
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    cursor: 'pointer',
                  }}
                >
                  {/* Invisible padding area */}
                  <Box
                    sx={{
                      position: 'absolute',
                      paddingTop: '50px',
                      paddingRight: '50px',
                      paddingLeft: '50px',
                      bottom: '0px',
                      width: '150%',
                      height: '100%',
                      backgroundColor: 'transparent',
                    }}
                  />

                  {/* Actual visible track */}
                  <Box
                    sx={{
                      width: `${TRACK_WIDTH}px`,
                      height: '8px',
                      backgroundColor: '#E3E6E8',
                      borderRadius: '999px',
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      onMouseDown={handleMouseDown}
                      sx={{
                        position: 'absolute',
                        height: '8px',
                        width: `${THUMB_WIDTH}px`,
                        backgroundColor: '#C7CA00',
                        borderRadius: '999px',
                        left: `${thumbLeft}px`,
                        transition: isDragging.current ? 'none' : 'left 0.2s',
                        cursor: 'grab',
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </ScrollFadeWrapper>
          )}

          {hasFooter && (
            <LinkWithIconComponent
              href={footerLink || undefined}
              iconSize="1rem"
              sx={{
                position: 'absolute',
                bottom: '0px',
                left: '50%',
                transform: 'translateX(-50%)',
                textAlign: 'center',
                fontSize: '1rem',
                fontWeight: 400,
                color: '#162F3B',
                maxWidth: '596px',
              }}
            >
              {footer}
            </LinkWithIconComponent>
          )}
        </Box>
      </Box>
    </ScrollFadeSection>
  );
};

export default ScrollingCardsComponent;
